import * as React from 'react';
import { getStoredSession } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import { useCitizenContext } from '@accenturecodefoundry/mfe-pot-citizen-context';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import { LifeEventLayout } from '../kit/LifeEventLayout';
import { BenefitCard } from '../kit/BenefitCard';
import { BenefitCardsSection } from '../kit/BenefitCardsSection';
import { SupportRow } from '../kit/SupportRow';
import { SupportSection } from '../kit/SupportSection';
import { assetBaseUrl } from '../asset-base-url';
import type { LifeEventPageModule, LifeEventPageProps } from './types';
import './JobLossPage.css';

/**
 * The original life event this whole family proves the pattern on. The two-path
 * hero cards at the top are job-loss-specific and not extracted to kit -- they
 * carry bespoke layout (eyebrow + icon well + CTA) that doesn't generalize
 * across other life events.
 */
export function JobLossPage({ lifeEventId, contentClient }: LifeEventPageProps) {
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);
  // CitizenContextProvider is mounted by App.tsx, so this hook always has a provider.
  // FES name takes priority over the session JWT name per track spec; province badge is
  // hidden when status !== 'ready' (loading or degraded).
  const { data: citizenData, status: citizenStatus } = useCitizenContext();
  const citizenName = citizenStatus === 'ready' ? citizenData?.name : undefined;
  const provinceLabel = citizenStatus === 'ready' ? citizenData?.address?.province : undefined;
  const sessionName = getStoredSession()?.name;
  const displayName = citizenName ?? sessionName;
  const headingText = displayName
    ? `${displayName}, ${t('journey.jobLoss.hero.headingSuffix')}`
    : t('journey.jobLoss.hero.headingSuffix');

  const headingSlot = (
    <section className="job-loss-heading">
      <div className="job-loss-heading__title-wrap">
        <h1 className="job-loss-heading__h1">{headingText}</h1>
        <div aria-hidden="true" className="job-loss-heading__accent-bar" />
      </div>
      <p className="job-loss-heading__subtitle">{t('journey.jobLoss.hero.subtitle')}</p>
    </section>
  );

  return (
    <LifeEventLayout lifeEventId={lifeEventId} contentClient={contentClient} headingSlot={headingSlot}>
      {/* 1. Two-path hero cards — job-loss-specific inline JSX */}
      <section aria-label={t('journey.jobLoss.heroEi.eyebrow') + ' / ' + t('journey.jobLoss.heroJobs.eyebrow')}>
        <div className="job-loss-heroes__grid">

          {/* Jobs path card — fully static (spec 010); the live job-match
              preview block was removed when the public-facing copy became
              broader than "roles that could be a good fit" alone. Card
              chrome (container + eyebrow + icon well + title + body + CTA
              anchor) is otherwise unchanged -- the "Explore jobs & training"
              anchor is still the destination hop into /job-bank. */}
          <div className="job-loss-hero">
            <span className="job-loss-hero__eyebrow job-loss-hero__eyebrow--jobs">
              {t('journey.jobLoss.heroJobs.eyebrow')}
            </span>
            <div aria-hidden="true" className="job-loss-hero__icon-well job-loss-hero__icon-well--jobs">
              <scds-icon name="briefcase" size="lg" />
            </div>
            <h2 className="job-loss-hero__title">{t('journey.jobLoss.heroJobs.title')}</h2>
            <p className="job-loss-hero__body">{t('journey.jobLoss.heroJobs.body')}</p>
            <a href="/job-bank" className="job-loss-hero__cta-btn">
              {t('journey.jobLoss.heroJobs.cta')}
              <scds-icon name="arrow-right" size="sm" aria-hidden="true" />
            </a>
          </div>

          {/* EI path card — no dollar figure intentionally (see CLAUDE.md) */}
          <a href="/employment-insurance" className="job-loss-hero">
            <span className="job-loss-hero__eyebrow job-loss-hero__eyebrow--ei">
              {t('journey.jobLoss.heroEi.eyebrow')}
            </span>
            <div aria-hidden="true" className="job-loss-hero__icon-well job-loss-hero__icon-well--ei">
              <scds-icon name="finance" size="lg" />
            </div>
            <h2 className="job-loss-hero__title">{t('journey.jobLoss.heroEi.title')}</h2>
            <p className="job-loss-hero__body">{t('journey.jobLoss.heroEi.body')}</p>
            <span className="job-loss-hero__cta-btn">
              {t('journey.jobLoss.heroEi.cta')}
              <scds-icon name="arrow-right" size="sm" aria-hidden="true" />
            </span>
          </a>
        </div>
      </section>

      {/* 4. Benefit cards — province badge from useCitizenContext; hidden when status !== 'ready' */}
      <BenefitCardsSection headingKey="journey.jobLoss.otherBenefits.heading">
        {/* Dental tile first (spec 010): newly separated workers losing
            employer dental coverage are the most directly affected by this
            life event, so ordering by relevance to the trigger. */}
        <BenefitCard
          iconName="shield"
          iconVariant="brand"
          badgeKey="journey.jobLoss.otherBenefits.dental.badge"
          titleKey="journey.jobLoss.otherBenefits.dental.title"
          noteKey="journey.jobLoss.otherBenefits.dental.note"
          href="https://www.canada.ca/en/services/benefits/dental/dental-care-plan.html"
          ctaLabelKey="journey.jobLoss.otherBenefits.dental.cta"
        />
        <BenefitCard
          iconName="finance"
          iconVariant="brand"
          badgeKey="journey.jobLoss.otherBenefits.cwb.badge"
          titleKey="journey.jobLoss.otherBenefits.cwb.title"
          noteKey="journey.jobLoss.otherBenefits.cwb.note"
          href="#"
          ctaLabelKey="journey.jobLoss.otherBenefits.cwb.cta"
        />
        <BenefitCard
          iconName="check-circle"
          iconVariant="brand"
          badgeKey="journey.jobLoss.otherBenefits.gstHst.badge"
          titleKey="journey.jobLoss.otherBenefits.gstHst.title"
          noteKey="journey.jobLoss.otherBenefits.gstHst.note"
          href="#"
          ctaLabelKey="journey.jobLoss.otherBenefits.gstHst.cta"
        />
        <BenefitCard
          iconName="home"
          iconVariant="neutral"
          badgeText={provinceLabel}
          titleKey="journey.jobLoss.otherBenefits.provincial.title"
          noteKey="journey.jobLoss.otherBenefits.provincial.note"
          href="#"
          ctaLabelKey="journey.jobLoss.otherBenefits.provincial.cta"
        />
      </BenefitCardsSection>

      {/* 5. Support section */}
      <SupportSection headingKey="journey.jobLoss.support.heading">
        <SupportRow
          iconName="heart"
          titleKey="journey.jobLoss.support.counsellor.title"
          bodyKey="journey.jobLoss.support.counsellor.body"
          href="#"
        />
        <SupportRow
          iconName="activity"
          titleKey="journey.jobLoss.support.pathways.title"
          bodyKey="journey.jobLoss.support.pathways.body"
          href="#"
        />
      </SupportSection>

    </LifeEventLayout>
  );
}

export const jobLossPage: LifeEventPageModule = { id: 'job-loss', Component: JobLossPage };
